import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { BinderFile, FileCategory, FileTemplate, PdfOptions, PrintBody, folderResponse } from 'src/app/models/binder-file';
import { environment } from 'src/environments/environment';
import { AppStateService } from '../app/app-state.service';
import { learningBinderFiles, learningFiles } from '../data/learning/learning-files';

@Injectable({
  providedIn: 'root'
})
export class FilesService {
  private BASE_URL = environment.landmark_API_URL + "/files";

  categories = signal<FileCategory[]>([]);
  viewedCategory = signal<FileCategory | null>(null);

  private http = inject(HttpClient);
  private appStateService = inject(AppStateService);

  constructor() { }

  uploadFile(binderId: number, file: File, category: any): Observable<BinderFile> {
    const formData = new FormData();
    formData.append('fileData', file, file.name);
    formData.append('fileName', file.name);
    formData.append('fileCategory', category.toString());

    formData.forEach((value, key) => {
      console.log(key + ' ' + value)
    });

  return this.http.post<BinderFile>(`${this.BASE_URL}/${binderId}`, formData);
  }

  uploadMultipleFiles(binderId: number, files: File[], category: number): Observable<BinderFile[]> {
    let fileBody = {
      fileData: files,
      fileCategory: category,
      binderId: binderId
    }

    const returnValue = this.http.post<BinderFile[]>(`${this.BASE_URL}/multiple/`, fileBody);
    return returnValue;
  }

  getFileCategories(binderId: number) {
    this.http.get<FileCategory[]>(`${this.BASE_URL}/binder/${binderId}`).subscribe((categories) => {
      this.categories.set(categories);
      if(categories.length > 0) {
        this.viewedCategory.set(categories[0]);
      }
    });
  }

  selectCategoryForGeneration(categoryId: number) {
    const category = this.categories().find(c => c.fileCategoryId === categoryId);
    if(category) {
      category.categorySelected = true;
    }
  }

  uploadSingleFile(binderId: number, file: File, category: number, fileId: string | null): Observable<BinderFile[]> {
    console.log('uploading single file', binderId, file, category, fileId);
    
    const formData = new FormData();
    formData.append('fileData', file);
    formData.append('fileCategory', category.toString());
    formData.append('binderId', binderId.toString());
    if (fileId && fileId !== null && fileId !== undefined && fileId !== '') {
      formData.append('fileId', fileId.toString());
    }
  
    console.log('formData', formData);
    console.log('category being sent:', formData.get('fileCategory'));
  
    return this.http.post<BinderFile[]>(`${this.BASE_URL}/single/`, formData);
  }


  attachClient(binderId: number, file: File, hsId: number): Observable<BinderFile> {
    const formData = new FormData();
    formData.append('fileData', file, file.name);
    formData.append('fileName', file.name);

    formData.forEach((value, key) => {
      console.log(key + ' ' + value)
    });

  return this.http.post<BinderFile>(`${this.BASE_URL}/attachclient/${binderId}/${hsId}`, formData);
  }

  convertFolder(binderId: number): Observable<folderResponse> {
    return this.http.post<folderResponse>(`${this.BASE_URL}/convert/${binderId}`, null);
  }

  generateFile(template: FileTemplate,masterJSON: any, docHash:string, config:PdfOptions, fileId?:string): Observable<BinderFile> {
    
    console.log('file json:', masterJSON)
    console.log('template:', template)

    const body: PrintBody = {
      data: masterJSON,
      docOptions: {
        DocName: template.templateName,
        hash: docHash,
        fileCategory: template.fileCategoryId,
        template: template.templatePath,
        config: config,
        fileTemplateId: template.fileTemplateId,
        footerTemplate: template.footerTemplate,
        headerTemplate: template.headerTemplate
      }
    };

    if(fileId) {
      return this.http.post<BinderFile>(`${this.BASE_URL}/regenerate/${fileId}`, body);
    }
    else {
      return this.http.post<BinderFile>(`${this.BASE_URL}/generate/`, body);
    }
  }

  deleteFile(fileId:string): Observable<BinderFile> {
    console.log('deleting...')
    return this.http.delete<BinderFile>(`${this.BASE_URL}/${fileId}`);
  }

  getMostRecentFile(categoryId: number): BinderFile | undefined {
    const category = this.categories().find(c => c.fileCategoryId === categoryId);
    if (!category || !category.binderFiles) return undefined;
  
    return category.binderFiles
      .slice()
      .sort((a, b) =>
        new Date(b.modifiedOn || b.createdOn).getTime() -
        new Date(a.modifiedOn || a.createdOn).getTime()
      )[0];
  }
  
  linkNewFileToCategory(binderFile: BinderFile, category: FileCategory) {
    const cat = this.categories().find(c => c.fileCategoryId === category.fileCategoryId);
    if(!cat) return;
    cat.binderFiles = [binderFile];
    cat.categorySelected = false;
    cat.generating = false;
    
    // Trigger signal update to ensure computed signals recalculate
    this.categories.update(c => [...c]);
  }

  mergeFilesIntoCategory(fileCategoryId: number, newFiles: BinderFile[]): void {
    const cats = this.categories();
    const category = cats.find(cat => cat.fileCategoryId === fileCategoryId);
  
    if (!category) return;
  
    const existingFiles = category.binderFiles ?? [];
  
    // Create a map of existing files by fileId for quick lookup
    const fileMap = new Map(existingFiles.map(file => [file.fileId, file]));
  
    for (const newFile of newFiles) {
      if (fileMap.has(newFile.fileId)) {
        // Update existing entry
        fileMap.set(newFile.fileId, { ...fileMap.get(newFile.fileId)!, ...newFile });
      } else {
        // Insert new file
        fileMap.set(newFile.fileId, newFile);
      }
    }
  
    // Convert map back to array
    category.binderFiles = Array.from(fileMap.values());
  
    this.categories.update(c => [...c]); // trigger signal update
  }

  // getBinderFiles(binderId: number) {
  //   if (this.appStateService.learningMode()) {
  //     this.files.set(learningBinderFiles);
  //   } else {
  //     console.log('getting binder files', binderId);
  //     this.http.get<BinderFile[]>(`${this.BASE_URL}/binder/${binderId}`).subscribe((files) => {
  //       this.files.set(files);
  //     });
  //   }
  // }
}
